import React from "react";
import propTypes from "prop-types";

/**
 * @typedef {import("./formBrain").Context} Context
 * @typedef {import("./formBrain").Validator} Validator
 * @typedef {import("./formBrain").FormError} FormError
 */

/**
 * @typedef {Object} StandardFieldProps
 *
 * @property {string} name The field name.
 * @property {string} label The human-readable name for the field. If a label is
 *                         not provided, one will be generated from the name.
 * @property {any} defaultValue The default value for the field.
 * @property {boolean} required True if this field is required.
 * @property {string} [className=""] The base className for the field.
 * @property {Validator | Validator[]} validation Validation methods.
 * @property {Function} onChange Default change handler.
 */

/**
 * Standard propTypes used by all fields.
 */
const fieldPropTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.string,
    required: propTypes.bool,
    className: propTypes.string,
    defaultValue: propTypes.any,
    validation: propTypes.oneOfType([
        propTypes.func,
        propTypes.arrayOf(propTypes.func),
    ]),
    onChange: propTypes.func,
};

/**
 * Hook: Run all the boilerplate work required by every field type.
 *
 * @param {StandardFieldProps} props The properties for the field. This should
 *   include at least the standard properties. Any additional properties will be
 *   added to the returned `fieldProps` value. To prevent this behaviour add
 *   "props" to the `skipProps` argument.
 * @param {Context} context The form context.
 * @param {string[]} [skipProps=[]] The names of properties to exclude from the
 *   returned `fieldProps` value. Specific properties from `props` can be listed
 *   here, as they are removed after `props` is merged into `fieldProps`.
 */
const useField = (
    {
        name: rawName,
        label: rawLabel,
        value: rawValue,
        required: rawRequired,
        className: rawClassName = "",
        validation,
        onChange,
        ...props
    },
    context,
    skipProps = [],
) => {
    const { form } = context;
    const name = getScopedName(rawName, context);
    const id = getId(rawName, context);
    const label = rawLabel || makeLabel(rawName);
    const value = rawValue || form.getValue(name, "");
    const required = context.required || rawRequired;
    const handleValidation = useValidation(name, label, validation, context);

    const handleChange = (e) => {
        const input = e.target;

        if (form.revising) {
            const passesConstraints = input.reportValidity();

            if (passesConstraints) {
                const errors = context.form.validateField(input);

                context.form.setErrors(input.name, errors);
            }
        }

        form.setValue(name, input.value);

        if (onChange) {
            onChange(e);
        }
    };
    const errors = useErrors(name, context);
    const hasErrors = errors.elements.length > 0;
    const className = `${rawClassName} ${hasErrors ? "error" : ""}`;

    let fieldProps = {
        id,
        name,
        value,
        required,
        className,
        "data-label": label,
        onChange: handleChange,
        onInvalid: handleValidation,
    };

    if (hasErrors) {
        fieldProps["data-error-msg"] = errors.messages;
        fieldProps["aria-describedby"] = errors.ids;
    }

    if (!skipProps.includes("props")) {
        fieldProps = {
            ...fieldProps,
            ...props
        };
    }

    fieldProps = skipProps.reduce((remainingProps, propName) => {
        if (propName === "props") return fieldProps;

        delete remainingProps[propName];
        return remainingProps;
    }, fieldProps);

    return {
        id,
        key: `${name}--input`,
        name,
        label,
        errors,
        required,
        value,
        className,
        handleChange,
        handleValidation,
        extraProps: props,
        fieldProps,
    };
};

/**
 * Create a new id for a field based on its name and location in the form.
 *
 * @param {string} name The full, scoped name of the field.
 * @param {Context} context The form context.
 * @param {string | number} [option] An optional identifier for this field. For
 *   example, when creating checkboxes or radio buttons where multiple fields
 *   would have the same name, and thus the same id.
 *
 * @return {string}
 */
const getId = (name, {id}, option) => {
    if (typeof option === "undefined") {
        return `${id}--${name}`;
    } else if (typeof option === "string") {
        return `${id}--${name}--${option.replace(/[^a-z0-9-]/ig, "_")}`;
    }
    return `${id}--${name}--${option}`;
};

/**
 * Create an internal name for an element respeting scope.
 *
 * If the element has a name like `price`, and is in a scoped fieldset called
 * `product_info`, this will return a name like `product_info[price]`.
 *
 * @param {string} name The field name.
 * @param {Context} context The form context.
 *
 * @return {string} The scoped name.
 */
const getScopedName = (name, { getScopedName }) => {
    return getScopedName(name);
};

/**
 * Converts the field name to a human-readable labe, for use in error messages.
 *
 * Form fields don't have reliable access to their labels, so this is needed to
 * create nice messages from the field name. It tries to split the given name
 * into words, and apply title casing.
 *
 * Most fields have a `label` property you can use if this function isn't
 * producing the right results, or if the actual label is very different than
 * the name.
 *
 * @param {string} name The un-scoped name of the field.
 *
 * @return {string}
 */
const makeLabel = (name) => name
    .replace(/[^a-z0-9]/gi, " ")
    .replace(/(\d+)/g, (match) => ` ${match} `)
    .replace(/([A-Z]|\b[a-z])/g, match => ` ${match.toUpperCase()}`)
    .replace(/  +/g, " ")
    .trim();

/**
 * Sets up error display for a field.
 *
 * @param {string} name The full, scoped field name.
 * @param {Context} context The form context.
 *
 * @typedef {Object} Errors
 * @property {any[]} elements JSX elements that can be displayed after the field
 *   containing the relevant error messages.
 * @property {string} messages Concatenated, pipe-delimited error messages.
 * @property {string} ids Concatenated, comma-delimited error element ids. These
 *   are in a format suitable for use in an `aria-describedby` property.
 *
 * @return {Errors}
 */
const useErrors = (name, { id, form, name: parent}) => {

    if (name === parent) {
        return {
            elements: [],
            messages: "",
            id: "",
        };
    }

    const errors = form.findErrors(name);
    const elements = [];
    const messages = [];
    const ids = [];

    if (errors.length) {

        errors.forEach(({message}, i) => {
            const key = `error-${i}`;
            const errorId = `${id}--${name}--${key}`;

            messages.push(message);
            ids.push(errorId);
            elements.push(
                <div
                    id={errorId}
                    key={key}
                    className="error error-inline"
                >
                    {message}
                </div>
            );
        });

    }

    return {
        elements,
        messages: messages.join(" | "),
        ids: ids.join(" "),
    };
};

/**
 * Sets up a field to use validation.
 *
 * This handles both passing field-level validators up to the form's central
 * validation collection, and creating an event handler to pass along errors
 * from the browser's constraint validation API.
 *
 * @param {string} name The full, scoped name of the field.
 * @param {string} label The human-readable name of the field.
 * @param {Validator[] | Validator} validation The field's validation methods.
 * @param {Context} context The form context.
 *
 * @return {Function} An event handle for the `onInvalid` event.
 */
const useValidation = (name, label, validation, { form, label: parentLabel, name: parent }) => {
    if (name === parent) {
        label = parentLabel;
    }

    if (validation) {
        const validators = Array.isArray(validation) ? validation : [validation];

        validators.forEach((validator) => {
            form.addValidator(name, validator);
        });
    }

    return (event) => {
        const {
            value,
            validity,
            min,
            max,
            minLength,
            maxLength,
            validationMessage,
        } = event.target;
        const errors = [];

        for (const id in validity) {
            const invalid = validity[id];

            if (!invalid || id === "valid") continue;

            switch(id) {
                case "valueMissing":
                    errors.push({
                        name,
                        message: `${label} is a required field.`,
                    });
                    break;

                case "typeMismatch":
                    errors.push({
                        name,
                        message: `${label} must be a valid ${event.target.type}.`,
                    });
                    break;

                case "patterMismatch":
                    errors.push({
                        name,
                        message: `"${value}" is not in the correct format for ${label}.`,
                    });
                    break;

                case "tooLong":
                    errors.push({
                        name,
                        message: `${label} can be at most ${maxLength} characters.`,
                    });
                    break;

                case "tooShort":
                    errors.push({
                        name,
                        message: `${label} must be at least ${minLength} characters.`,
                    });
                    break;

                case "underflow":
                case "rangeUnderflow":
                    errors.push({
                        name,
                        message: `${label} cannot be less than ${min}.`,
                    });
                    break;

                case "overflow":
                case "rangeOverflow":
                    errors.push({
                        name,
                        message: `${label} cannot be greater than ${max}.`,
                    });
                    break;

                default:
                    errors.push({
                        name,
                        message: validationMessage,
                    });
                    break;
            }
        }

        form.setErrors(name, errors);
        event.preventDefault();
    };
};

export default useField;

export {
    fieldPropTypes,
    getId,
    getScopedName,
    makeLabel,
    useErrors,
    useValidation,
};
